import CardGiftcardOutlined from "@mui/icons-material/CardGiftcardOutlined";
import CloseIcon from "@mui/icons-material/Close";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import IconButton from "@mui/material/IconButton";
import { useEffect, useRef, useState } from "react";
import { TY_BACK_LABELS } from "@/constants/thankYouNotes";
import type { ThankYouNote } from "@/interfaces/thankYouNotes";
import { formatDateLong } from "@/utils/date-format";
import "./thankYouNoteCard.css";

interface ThankYouNoteCardProps {
  note: ThankYouNote;
  isFavorite: boolean;
  onToggleFavorite: (id: number) => void;
}

function GiftThumb({ giftImg, giftName }: { giftImg: string | null; giftName: string | null }) {
  const [imgFailed, setImgFailed] = useState(false);
  const [lastGiftImg, setLastGiftImg] = useState(giftImg);
  // Reset the failure flag when this instance starts rendering a different note's image,
  // without a useEffect: adjust state during render per the React docs pattern for derived state.
  if (giftImg !== lastGiftImg) {
    setLastGiftImg(giftImg);
    setImgFailed(false);
  }

  // giftImg can fail to load even once GFG-234 wires it to a real Shopify CDN URL (product
  // removed, file swapped, image expired) — treat a load failure the same as no image at all.
  if (!giftImg || imgFailed) {
    return (
      <div className="ty-gift-placeholder">
        <CardGiftcardOutlined className="text-rodeo-dust" style={{ fontSize: "40px" }} />
      </div>
    );
  }
  return (
    <img
      src={giftImg}
      alt={giftName ?? "Gift"}
      className="ty-gift-img"
      onError={() => setImgFailed(true)}
    />
  );
}

export default function ThankYouNoteCard({
  note,
  isFavorite,
  onToggleFavorite,
}: ThankYouNoteCardProps) {
  const [flipped, setFlipped] = useState(false);
  const frontRef = useRef<HTMLDivElement>(null);
  const backRef = useRef<HTMLDivElement>(null);
  const showDetailsRef = useRef<HTMLButtonElement>(null);
  const flipBackRef = useRef<HTMLButtonElement>(null);
  // Guards the focus move below so it only fires on a REAL flip. Tracking "has this effect run
  // before" is not enough: StrictMode mounts, cleans up and mounts again, so the effect body runs
  // twice on mount and the second run would steal focus into a card in the middle of the grid,
  // scrolling the page down the moment the list rendered. Comparing the previous `flipped` value
  // is immune to that — on both mount runs it still equals the current one.
  const prevFlippedRef = useRef(flipped);

  // backface-visibility only hides the non-showing face visually — the DOM node stays
  // tabbable and screen-reader-reachable otherwise. `inert` (imperative: the installed
  // React/DOM type defs don't expose it as a JSX prop yet) plus `aria-hidden` removes the
  // hidden face from both the tab order and the accessibility tree.
  useEffect(() => {
    if (frontRef.current) frontRef.current.inert = flipped;
    if (backRef.current) backRef.current.inert = !flipped;

    if (prevFlippedRef.current === flipped) return;
    prevFlippedRef.current = flipped;
    // The button the user just pressed now sits in the face that turned inert, so the browser
    // blurs it and focus falls to <body> — a keyboard user is thrown back to the top of the page
    // on every flip (WCAG 2.4.3). Move focus to the button on the face that became visible.
    // Order matters: this runs AFTER the `inert` flags above, since focus() is a no-op on an
    // element inside an inert subtree and re-applying `inert` afterwards would drop it again.
    const nextFocus = flipped ? flipBackRef.current : showDetailsRef.current;
    nextFocus?.focus();
  }, [flipped]);

  return (
    <div className="ty-card">
      <div className={`ty-card-inner${flipped ? " flipped" : ""}`}>
        <div ref={frontRef} className="ty-face ty-face-front" aria-hidden={flipped || undefined}>
          <div className="ty-card-top">
            <IconButton
              aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
              aria-pressed={isFavorite}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onToggleFavorite(note.id);
              }}
              sx={{
                width: 32,
                height: 32,
                color: isFavorite ? "var(--color-dark-dust)" : "var(--color-rodeo-dust)",
              }}
            >
              {isFavorite ? (
                <FavoriteIcon sx={{ fontSize: 18 }} />
              ) : (
                <FavoriteBorderIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
            <IconButton
              ref={showDetailsRef}
              aria-label="Show details"
              onClick={() => setFlipped(true)}
              sx={{ width: 32, height: 32, color: "var(--color-rodeo-dust)" }}
            >
              <InfoOutlined sx={{ fontSize: 18 }} />
            </IconButton>
          </div>

          <div className="font-crimson text-rodeo-black ty-msg">&ldquo;{note.message}&rdquo;</div>
          <div className="font-bold text-rodeo-black ty-recipient">{note.recipient}</div>
          <div className="text-graphite ty-submitted">{formatDateLong(note.submitted)}</div>
        </div>

        {/* Back face. Pre-rotated 180deg so it faces the viewer once the inner flips. */}
        <div ref={backRef} className="ty-face ty-face-back" aria-hidden={!flipped || undefined}>
          <IconButton
            ref={flipBackRef}
            className="ty-back-close"
            aria-label="Flip back"
            onClick={() => setFlipped(false)}
            sx={{ width: 32, height: 32, color: "var(--color-rodeo-dust)" }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>

          <div className="ty-back-top">
            <div className="ty-gift">
              <GiftThumb giftImg={note.giftImg} giftName={note.giftName} />
            </div>
          </div>

          <div className="ty-back-bottom">
            <dl className="ty-back-details">
              <dt>{TY_BACK_LABELS.campaign}</dt>
              <dd className="text-rodeo-black">{note.campaign}</dd>

              <dt>{TY_BACK_LABELS.redeemed}</dt>
              <dd className="text-rodeo-black">{formatDateLong(note.redeemed)}</dd>

              <dt>{TY_BACK_LABELS.gift}</dt>
              <dd className="text-rodeo-black">{note.giftName ?? "—"}</dd>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}
